/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { ReactElement, ReactNode } from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, HashRouter } from 'react-router-dom';

import AppRoutes from '@/AppRoutes';
import ApiStatus from '@/components/ApiStatus';
import AppInfoDialog from '@/components/AppInfo';
import LoadingOrError from '@/components/LoadingOrError';
import SomethingWentWrong from '@/components/SomethingWentWrong';
import SubmitFeedbackDialog from '@/components/SubmitFeedback';
// import UpdateElectron from '@/components/update';
// import UpdateApp from '@/components/UpdateApp';
import VersionMonitor from '@/components/VersionMonitor';
import { VirtualKeyboard, VirtualKeyboardProvider } from '@/components/VirtualKeyboard';
import { ButtonDialogBoxProvider } from '@/hooks/useButtonDialogBox';
import { DialogBoxProvider } from '@/hooks/useDialogBox';
import useGetAppVersion from '@/hooks/useGetAppVersion';
import useLanguage from '@/hooks/useLanguage';
import { SnackbarProvider } from '@/hooks/useSnackbar';
import logger from '@/logger';
import { useApplyTheme } from '@/theme/useApplyTheme';
import { isDesktop } from '@/utils/helper';

import initPostHog from './posthogConfig';
import { getDesktopVersion, getVersion } from './utils';

type CustomRouterProperties = {
  children: ReactNode;
};

function CustomRouter({ children }: CustomRouterProperties) {
  const isElectronDesktop = useMemo(() => isDesktop(), []);
  if (isElectronDesktop) {
    return <HashRouter>{children}</HashRouter>;
  }
  return <BrowserRouter>{children}</BrowserRouter>;
}

export default function App(): ReactElement {
  useApplyTheme();
  useGetAppVersion();
  const [showVersion, setShowVersion] = useState<boolean>(!!localStorage.getItem('showVersion'));
  useEffect(() => {
    window.enableReveal = () => {
      logger.info('Reveal enabled!');
      localStorage.setItem('revealEnabled', '1');
    };
    window.disableReveal = () => {
      logger.info('Reveal disabled!');
      localStorage.removeItem('revealEnabled');
    };
    window.showVersion = () => {
      logger.info('Version display enabled!');
      localStorage.setItem('showVersion', '1');
      setShowVersion(true);
    };
    window.hideVersion = () => {
      logger.info('Version display disabled!');
      localStorage.removeItem('showVersion');
      setShowVersion(false);
    };
    window.enableWarning = () => {
      logger.info('Warning enabled!');
      localStorage.setItem('showWarning', '1');
    };
    window.disableWarning = () => {
      logger.info('Warning disbled!');
      localStorage.removeItem('showWarning');
    };
  }, []);

  useEffect(() => {
    if (isDesktop())
      void getDesktopVersion().then((version) => {
        initPostHog(version);
      });
    else {
      const version = getVersion();
      initPostHog(version);
    }
  }, []);

  useLanguage();

  // useFirebaseRemoteConfig();
  // useFirebaseMessaging();
  // const { trackEvent } = useFirebaseAnalytics();

  // useEffect(() => {
  //   trackEvent({ name: 'app_start', params: {} });
  // }, [trackEvent]);

  return (
    <ErrorBoundary
      FallbackComponent={SomethingWentWrong}
      // eslint-disable-next-line react/jsx-handler-names
      onError={(error: Error) => {
        logger.error(error);
        // Do something with the error, e.g. log to an external API like firebase or monitoring tool
      }}
    >
      <VirtualKeyboardProvider>
        <CustomRouter>
          <Suspense fallback={<LoadingOrError />}>
            <SnackbarProvider>
              <DialogBoxProvider>
                <ButtonDialogBoxProvider>
                  <AppRoutes />
                  <ApiStatus />
                  {isDesktop() && <AppInfoDialog />}
                  {isDesktop() && <SubmitFeedbackDialog />}
                  {/* {isDesktop() && <UpdateApp />} */}
                </ButtonDialogBoxProvider>
              </DialogBoxProvider>
            </SnackbarProvider>
          </Suspense>
        </CustomRouter>
        {/* <ReactQueryDevtools position="bottom" /> */}
        {showVersion && <VersionMonitor />}
        {/* {isDesktop() && <UpdateElectron />} */}
        <VirtualKeyboard />
      </VirtualKeyboardProvider>
    </ErrorBoundary>
  );
}
